import { useEffect, useMemo, useRef, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';
import ConsentBanner from './components/ConsentBanner.jsx';
import { LEGAL_ROUTES, LegalFooterLinks, LegalPage } from './legal/LegalPages.jsx';
import { openCookieSettings } from './services/consent.js';
import ChatPanel from './components/ChatPanel.jsx';
import RightPanel from './components/RightPanel.jsx';
import GoalsTab from './components/GoalsTab.jsx';
import HabitsTab from './components/HabitsTab.jsx';
import HistoryTab from './components/HistoryTab.jsx';
import InsightsTab from './components/InsightsTab.jsx';
import MoodWidget from './components/MoodWidget.jsx';
import { useElizaData } from './hooks/useElizaData.js';
import { usePushNotifications } from './hooks/usePushNotifications.js';

const MOBILE_VIEWS = [
  { key: 'chat', label: 'Chat', icon: '✎' },
  { key: 'goals', label: 'Goals', icon: '◎' },
  { key: 'habits', label: 'Habits', icon: '☘' },
  { key: 'history', label: 'History', icon: '〜' },
  { key: 'insights', label: 'Insights', icon: '✦' }
];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 900px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export default function App() {
  const [authState, setAuthState] = useState({ status: 'loading', user: null });
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const user = await getCurrentUser();
        if (!cancelled) setAuthState({ status: 'signedIn', user });
      } catch {
        if (!cancelled) setAuthState({ status: 'signedOut', user: null });
      }
    };
    check();
    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signInWithRedirect') check();
      else if (payload.event === 'signedOut') setAuthState({ status: 'signedOut', user: null });
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Legal pages are public routes — rendered before the auth gate.
  if (LEGAL_ROUTES[path]) {
    return (
      <>
        <LegalPage path={path} />
        <ConsentBanner />
      </>
    );
  }

  if (authState.status === 'loading') {
    return <Splash>Just a moment…</Splash>;
  }

  if (authState.status === 'signedOut') {
    return (
      <>
        <Splash
          footer={<LegalFooterLinks className="legal-links legal-links--splash" />}
        >
          <h1 className="splash-title">Eliza</h1>
          <p className="splash-sub">Sign in to continue.</p>
          <button className="primary-btn" onClick={() => signInWithRedirect({ provider: 'Google' })}>
            Sign in with Google
          </button>
        </Splash>
        <ConsentBanner />
      </>
    );
  }

  return <SignedInApp user={authState.user} />;
}

function SignedInApp({ user }) {
  const eliza = useElizaData();
  const isDesktop = useIsDesktop();
  const [mobileView, setMobileView] = useState('chat');
  const [menuOpen, setMenuOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem('eliza-dashboard') !== 'closed';
  });

  const toggleDashboard = () => {
    setDashboardOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('eliza-dashboard', next ? 'open' : 'closed');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const push = usePushNotifications({
    savePrefs: eliza.savePrefs,
    prefs: eliza.data.prefs
  });

  const mutators = useMemo(
    () => ({
      saveGoal: eliza.saveGoal,
      deleteGoal: eliza.deleteGoal,
      saveHabit: eliza.saveHabit,
      deleteHabit: eliza.deleteHabit,
      checkInHabit: eliza.checkInHabit,
      toggleHabitPause: eliza.toggleHabitPause,
      saveMood: eliza.saveMood,
      generateInsights: eliza.generateInsights
    }),
    [eliza]
  );

  if (eliza.loading) {
    return <Splash>Loading your stuff…</Splash>;
  }

  if (eliza.error && !eliza.data.goals.length && !eliza.data.habits.length) {
    return (
      <Splash>
        <p>Couldn't reach the server. Try refreshing.</p>
        <p className="muted">{eliza.error.message}</p>
      </Splash>
    );
  }

  return (
    <div className="app-root">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="app-bar">
        <h1 className="app-bar__title">Eliza</h1>
        <div className="app-bar__right">
          {!eliza.online && (
            <span className="app-bar__offline" role="status">
              offline
            </span>
          )}
          {isDesktop && (
            <button
              className="link-btn app-bar__dashboard-toggle"
              onClick={toggleDashboard}
              aria-expanded={dashboardOpen}
            >
              {dashboardOpen ? 'Hide dashboard ›' : '‹ Show dashboard'}
            </button>
          )}
          <AppMenu
            open={menuOpen}
            setOpen={setMenuOpen}
            push={push}
            refresh={eliza.refresh}
            user={user}
          />
        </div>
      </header>

      {isDesktop ? (
        <main
          id="main-content"
          className={`app-main app-main--split${dashboardOpen ? '' : ' app-main--collapsed'}`}
        >
          <ChatPanel data={eliza.data} chat={eliza.chat} online={eliza.online} />
          <RightPanel data={eliza.data} mutators={mutators} />
        </main>
      ) : (
        <>
          <main id="main-content" className="app-main app-main--mobile">
            {mobileView === 'chat' ? (
              <ChatPanel data={eliza.data} chat={eliza.chat} online={eliza.online} />
            ) : (
              <div className="mobile-tab-view">
                <MoodWidget onSave={mutators.saveMood} />
                {mobileView === 'goals' && (
                  <GoalsTab
                    goals={eliza.data.goals}
                    saveGoal={mutators.saveGoal}
                    deleteGoal={mutators.deleteGoal}
                  />
                )}
                {mobileView === 'habits' && (
                  <HabitsTab
                    habits={eliza.data.habits}
                    saveHabit={mutators.saveHabit}
                    deleteHabit={mutators.deleteHabit}
                    checkInHabit={mutators.checkInHabit}
                    toggleHabitPause={mutators.toggleHabitPause}
                  />
                )}
                {mobileView === 'history' && <HistoryTab moods={eliza.data.moods} />}
                {mobileView === 'insights' && (
                  <InsightsTab
                    insights={eliza.data.insights}
                    generateInsights={mutators.generateInsights}
                  />
                )}
              </div>
            )}
          </main>
          <nav className="bottom-nav" aria-label="Primary">
            {MOBILE_VIEWS.map((v) => (
              <button
                key={v.key}
                aria-current={mobileView === v.key ? 'true' : undefined}
                onClick={() => setMobileView(v.key)}
                className={`bottom-nav__btn${mobileView === v.key ? ' bottom-nav__btn--active' : ''}`}
              >
                <span className="bottom-nav__icon" aria-hidden="true">
                  {v.icon}
                </span>
                <span className="bottom-nav__label">{v.label}</span>
              </button>
            ))}
          </nav>
        </>
      )}
      <ConsentBanner />
    </div>
  );
}

function AppMenu({ open, setOpen, push, refresh, user }) {
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onClickOutside);
    };
  }, [open, setOpen]);

  return (
    <div className="app-bar__menu-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        className="link-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu"
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <div className="app-bar__menu">
          <button
            className="app-bar__menu-item"
            onClick={async () => {
              if (push.subscription) await push.disable();
              else await push.enable();
              setOpen(false);
            }}
            disabled={!push.supported}
          >
            {push.subscription ? 'Disable check-ins' : 'Enable check-ins'}
          </button>
          <button
            className="app-bar__menu-item"
            onClick={() => {
              refresh();
              setOpen(false);
            }}
          >
            Refresh
          </button>
          <button
            className="app-bar__menu-item"
            onClick={() => {
              setOpen(false);
              openCookieSettings();
            }}
          >
            Cookie settings
          </button>
          <a className="app-bar__menu-item" href="/privacy">
            Privacy &amp; legal
          </a>
          <button
            className="app-bar__menu-item"
            onClick={() => {
              signOut();
              setOpen(false);
            }}
          >
            Sign out — {user?.signInDetails?.loginId ?? user?.username}
          </button>
        </div>
      )}
    </div>
  );
}

function Splash({ children, footer }) {
  return (
    <div className="splash">
      <main className="splash-card">{children}</main>
      {footer && <footer className="splash-footer">{footer}</footer>}
    </div>
  );
}
